import { useState } from 'react'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import type { RoleUserResponse } from '@portal/pkg-api/gen/admin-console'
import { useAddRoleMembers } from '../mutations'
import { RoleQueries } from '../queries'

function matches(user: RoleUserResponse, term: string) {
  if (!term) return true
  return (
    `${user.givenName} ${user.familyName}`.toLowerCase().includes(term) ||
    user.email.toLowerCase().includes(term)
  )
}

/**
 * The candidates come from the server's assignable-users list, which has already removed the
 * role's existing members and anything the caller's licence hides. Nothing is filtered here.
 */
export function AddUsersDialog({ roleId, onClose }: { roleId: number; onClose: () => void }) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<ReadonlySet<number>>(new Set())
  const { data: candidates } = useSuspenseQuery(RoleQueries.assignableUsers(roleId))
  const addMembers = useAddRoleMembers()

  const visible = candidates.filter((user) => matches(user, search.toLowerCase().trim()))

  const toggle = (id: number) =>
    setSelected((previous) => {
      const next = new Set(previous)
      if (!next.delete(id)) next.add(id)
      return next
    })

  const submit = () =>
    addMembers.mutate(
      { id: roleId, userIds: [...selected] },
      {
        onSuccess: () => {
          enqueueSnackbar(t('roles.users.usersAdded', { count: selected.size }), {
            variant: 'success',
          })
          onClose()
        },
        onError: () => enqueueSnackbar(t('roles.users.addFailed'), { variant: 'error' }),
      },
    )

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('roles.users.addTitle')}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1}>
          <TextField
            size="small"
            variant="outlined"
            placeholder={t('roles.users.searchCandidates')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          {visible.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', py: 3, textAlign: 'center' }}
            >
              {t(candidates.length === 0 ? 'roles.users.noCandidates' : 'roles.users.noResults')}
            </Typography>
          ) : (
            <List dense sx={{ maxHeight: 360, overflow: 'auto' }}>
              {visible.map((user) => (
                <ListItemButton key={user.id} onClick={() => toggle(user.id)} dense>
                  <Checkbox
                    edge="start"
                    size="small"
                    checked={selected.has(user.id)}
                    tabIndex={-1}
                  />
                  <ListItemText
                    primary={t('displayName', {
                      givenName: user.givenName,
                      familyName: user.familyName,
                    })}
                    secondary={user.email.toLowerCase()}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('roles.users.cancel')}</Button>
        <Button
          variant="contained"
          disabled={selected.size === 0 || addMembers.isPending}
          onClick={submit}
        >
          {t('roles.users.add')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
